import VueRouter from 'vue-router'
import { RouteConfig } from 'vue-router/types/router'

import { sync } from 'vuex-router-sync'

import { PatternLibraryRoute } from './routes/pattern-library/pattern-library'
import { PlatformRoute } from './routes/platform/platform'
import { LoginRoute } from './routes/login/login'
import { RequestResetPasswordRoute, ResetPasswordRoute } from './routes/reset-password/reset-password'
import { MemberDetails } from './routes/members/details'
import { CreateMember } from './routes/members/create-member'
import { Content } from './routes/content/content'
import { Club } from './routes/club/club'
import { WorkoutBuilder } from './routes/content/workout-builder'

import MemberActivitiesModule from './state/modules/member-activities'
import BlogModule from '@src/state/modules/blog'
import CheckupsModule from '@src/state/modules/checkups'

import { ArrangementSettings } from '@src/routes/club/arrangement-settings'
import { Schedule } from '@src/routes/club/schedule'
import { ClubInfo } from '@src/routes/club/club-info'
import { GeneralSettings } from '@src/routes/club/general-settings'
import { ClubStatistics } from '@src/routes/club/statistics'

import Store from './state/store'
import { TrainingPlanBuilder } from './routes/content/training-plan-builder'
import { Workouts } from '@src/routes/content/workouts'
import { TrainingPlans } from '@src/routes/content/training-plans'
import Exercises from '@src/routes/content/exercises'
import { MemberStatus as Status } from '@src/routes/members/status'
import { MemberNotes } from '@src/routes/members/notes'
import MemberOnboarding from '@src/routes/members/components/onboarding/onboarding'
import { ActiveTrainingsWorkouts } from '@src/routes/members/components/active-trainings-workouts/active-trainings-workouts'
import CompletedOnboarding from '@src/routes/members/components/onboarding/completed-onboarding'
import { Checkups } from '@src/routes/members/checkups'
import CheckupWizard from '@src/routes/members/components/checkups/checkup-wizard'
import CompletedCheckup from '@src/routes/members/components/checkups/completed-checkup'
import MemberAccount from '@src/routes/members/account'
import { Blog } from '@src/routes/blog/blog'
import { TrainingHistory } from '@src/routes/members/components/results-history/history'
import { WizardHealthLimitations } from '@src/routes/members/components/wizard/health-limitations'
import { WizardMeasurements } from '@src/routes/members/components/wizard/measurements'
import { WizardHealthGoals } from '@src/routes/members/components/wizard/health-goals'
import { WizardTrainings } from '@src/routes/members/components/wizard/trainings'
import { WizardMemberNotes } from '@src/routes/members/components/wizard/notes'
import { AccountDetails } from '@src/routes/members/components/wizard/account-details'
import TrainerAccount from '@src/routes/account/trainer-account'
import { MembersSupport, PlatformsSupport } from '@src/routes/support/components/platforms/platforms-support'
import SessionManager from '@src/lib/session_manager'
import { EmailUpdate } from '@src/routes/email-update/email-update'
import { WorkoutViewer } from '@src/routes/content/components/workout-viewer/workout-viewer'
import { HealthLimitations } from '@src/routes/members/components/health-limitations/health-limitations'
import { Measurements } from '@src/routes/members/measurements'
import { MemberPlatformRoute } from '@src/routes/member-platform/member-platform'
import { ReadonlyCheckups } from '@src/routes/member-platform/components/checkups/checkups'
import { ReadonlyStatus } from '@src/routes/member-platform/components/status/status'
import { ReadonlyActivities } from '@src/routes/member-platform/components/activities/activities'
import { ReadonlyHealthLimitations } from '@src/routes/member-platform/components/health-limitations/health-limitations'
import { ReadonlyMeasurements } from '@src/routes/member-platform/components/body-measurements/measurements'
import MemberPersonalAccount from '@src/routes/member-platform/components/account/account'
import { MemberActivities } from '@src/routes/members/member-activities'
import { DownloadMobileApp } from '@src/routes/mobile-app/mobile-app'
import { MemberActiveTrainingsWorkouts } from '@src/routes/member-platform/components/active-trainings-workouts/active-trainings-workouts'
import { MemberTrainingPlanBuilder } from '@src/routes/member-platform/components/active-trainings-workouts/training-plan-builder'
import ReadonlyCheckupWizard from '@src/routes/member-platform/components/checkups/checkup-wizard'
import { MemberWizardMeasurements } from '@src/routes/member-platform/components/checkups/measurements'
import { MemberWizardHealthLimitations } from '@src/routes/member-platform/components/checkups/health-limitations'
import { Contact } from '@src/routes/support/components/contact/contact'
import { TrainingPlanViewer } from '@src/routes/content/components/training-plan-viewer/training-plan-viewer'
import ResultsHistory from '@src/routes/members/components/results-history/results-history'
import { ComposeMessage } from '@src/routes/messaging/compose-message'
import { CookieBanner } from '@src/components/cookie-banner/cookie-banner'
import { NewMember } from '@src/routes/members/components/create-member/new-member'
import { ConnectMobileMember } from '@src/routes/members/components/create-member/connect-mobile-member'
import { ExercisesSupport } from '@src/routes/support/components/exercises/exercises-support'
import { Support } from '@src/routes/support/support'
import { defaultMemberFilters, defaultTrainingPlanFilters, defaultWorkoutFilters } from '@src/state/dafaults'
import { UserState } from '@src/data/user'
import { Classes } from '@src/routes/content/classes'
import { MyClub } from '@src/routes/member-platform/components/my-club'
import { PaginatedMembersList } from '@src/routes/members/paginated-members-list'
import { WorkoutPlayground } from '@src/routes/content/workout-playground/workout-playground'

class Router extends VueRouter {

  constructor (store: Store) {
    super({
      mode: 'history',
      routes: [
        setupRoute(store, '/', { component: LoginRoute }),

        setupRoute(store, '/trainers/reset-password/:token', { component: ResetPasswordRoute }),
        setupRoute(store, '/members/reset-password/:token', { component: ResetPasswordRoute, role: ['member'] }),
        setupRoute(store, '/trainers/reset-password', { component: RequestResetPasswordRoute }),
        setupRoute(store, '/trainers/email-update/:token', { component: EmailUpdate }),
        setupRoute(store, '/members/email-update/:token', { component: EmailUpdate, role: ['member'] }),

        setupRoute(store, '/trainers/platform', {
          component: PlatformRoute, role: ['admin', 'assistant', 'therapist', 'trainer'],
          children: [
            setupRoute(store, 'members', { component: PaginatedMembersList, moduleName: 'members', role: ['admin', 'assistant', 'therapist', 'trainer'] }),
            setupRoute(store, 'members/new', { component: CreateMember, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
            setupRoute(store, 'members/create', { component: NewMember, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
            setupRoute(store, 'members/connect-mobile', { component: ConnectMobileMember, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
            setupRoute(store, 'members/:id', {
              component: MemberDetails,
              children: [
                setupRoute(store, 'member_activities', {
                  component: MemberActivities,
                  module: MemberActivitiesModule,
                  role: ['admin', 'assistant', 'therapist', 'trainer']
                }),
                setupRoute(store, 'status', { component: Status, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
                setupRoute(store, 'notes', { component: MemberNotes, moduleName: 'members', role: ['admin', 'assistant', 'therapist', 'trainer'] }),
                setupRoute(store, 'checkups', { component: Checkups, module: CheckupsModule, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
                setupRoute(store, 'training', {
                  component: ActiveTrainingsWorkouts,
                  moduleName: 'content',
                  role: ['admin', 'assistant', 'therapist', 'trainer']
                }),
                setupRoute(store, 'account', { component: MemberAccount, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
                setupRoute(store, 'health', { component: HealthLimitations, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
                setupRoute(store, 'body-measurements', { component: Measurements, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
                setupRoute(store, 'history', {
                  component: ResultsHistory, role: ['admin', 'assistant', 'therapist', 'trainer'],
                  children: [
                    setupRoute(store, '', { component: TrainingHistory, role: ['admin', 'assistant', 'therapist', 'trainer'] })
                  ]
                })
              ]
            }),
            setupRoute(store, 'content', {
              component: Content, moduleName: 'content',
              children: [
                setupRoute(store, 'exercises', { component: Exercises, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
                setupRoute(store, 'workouts', { component: Workouts, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
                setupRoute(store, 'trainingplan', { component: TrainingPlans, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
                setupRoute(store, 'classes', { component: Classes, role: ['admin', 'assistant', 'therapist', 'trainer'] })
              ]
            }),
            setupRoute(store, 'club', {
              component: Club,
              children: [
                setupRoute(store, 'arrangement', { component: ArrangementSettings, moduleName: 'content', role: ['admin'] }),
                setupRoute(store, 'schedule', { component: Schedule, role: ['admin'] }),
                setupRoute(store, 'club-info', { component: ClubInfo, role: ['admin'] }),
                setupRoute(store, 'general-settings', { component: GeneralSettings, role: ['admin'] }),
                setupRoute(store, 'statistics', { component: ClubStatistics, role: ['admin'] })
              ]
            }),
            setupRoute(store, 'blog', { component: Blog, module: BlogModule, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
            setupRoute(store, 'account', { component: TrainerAccount, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
            setupRoute(store, 'support', {
              component: Support, moduleName: 'content', role: ['admin', 'assistant', 'therapist', 'trainer'], children: [
                setupRoute(store, 'contact', { component: Contact, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
                setupRoute(store, 'contact-support', { component: PlatformsSupport, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
                setupRoute(store, 'support-ticket*', { component: ExercisesSupport, role: ['admin', 'assistant', 'therapist', 'trainer'] })
              ]
            }),
            setupRoute(store, 'messaging', { component: ComposeMessage, role: ['admin', 'assistant', 'therapist', 'trainer'] })
          ]
        }),
        setupRoute(store, '/trainers/workout', { component: WorkoutBuilder, moduleName: 'content', role: ['admin', 'therapist', 'trainer'] }),
        setupRoute(store, '/trainers/workout/edit/:id', { component: WorkoutBuilder, moduleName: 'content', role: ['admin','therapist', 'trainer'] }),
        setupRoute(store, '/trainers/workout/view/:id', {
          component: WorkoutViewer,
          role: ['admin', 'assistant', 'therapist', 'trainer']
        }),
        setupRoute(store, '/trainers/trainingPlan', {
          component: TrainingPlanBuilder,
          moduleName: 'content',
          role: ['admin', 'therapist', 'trainer']
        }),
        setupRoute(store, '/trainers/trainingPlan/edit/:id', {
          component: TrainingPlanBuilder,
          moduleName: 'content',
          role: ['admin', 'therapist', 'trainer']
        }),
        setupRoute(store, '/trainers/trainingPlan/view/:id', {
          component: TrainingPlanViewer,
          moduleName: 'content',
          role: ['admin', 'assistant', 'therapist', 'trainer']
        }),
        setupRoute(store, '/trainers/onboarding/:id/:checkupId', {
          component: MemberOnboarding, role: ['admin', 'assistant', 'therapist', 'trainer'],
          children: [
            setupRoute(store, 'account', { component: AccountDetails, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
            setupRoute(store, 'health', { component: WizardHealthLimitations, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
            setupRoute(store, 'body-measurements', { component: WizardMeasurements, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
            setupRoute(store, 'goals', { component: WizardHealthGoals, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
            setupRoute(store, 'training', { component: WizardTrainings, moduleName: 'content', role: ['admin', 'assistant', 'therapist', 'trainer'] }),
            setupRoute(store, 'notes', { component: WizardMemberNotes, moduleName: 'members', role: ['admin', 'assistant', 'therapist', 'trainer'] }),
            setupRoute(store, 'finish', { component: CompletedOnboarding, role: ['admin', 'assistant', 'therapist', 'trainer'] })
          ]
        }),
        setupRoute(store, '/trainers/checkup/:id/:checkupId', {
          component: CheckupWizard, role: ['admin', 'assistant', 'therapist', 'trainer'],
          children: [
            setupRoute(store, 'health', { component: WizardHealthLimitations, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
            setupRoute(store, 'body-measurements', { component: WizardMeasurements, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
            setupRoute(store, 'goals', { component: WizardHealthGoals, role: ['admin', 'assistant', 'therapist', 'trainer'] }),
            setupRoute(store, 'training', { component: WizardTrainings, moduleName: 'content', role: ['admin', 'assistant', 'therapist', 'trainer'] }),
            setupRoute(store, 'notes', { component: WizardMemberNotes, moduleName: 'members', role: ['admin', 'assistant', 'therapist', 'trainer'] }),
            setupRoute(store, 'finish', { component: CompletedCheckup, role: ['admin', 'assistant', 'therapist', 'trainer'] })
          ]
        }),

        setupRoute(store, '/members/workout', { component: WorkoutBuilder, moduleName: 'content', role: ['member'] }),
        setupRoute(store, '/members/workout/edit/:id', { component: WorkoutBuilder, moduleName: 'content', role: ['member'] }),
        setupRoute(store, '/members/workout/view/:id', { component: WorkoutViewer, role: ['member'] }),
        setupRoute(store, '/members/trainingPlan', {
          component: MemberTrainingPlanBuilder,
          moduleName: 'content',
          role: ['member']
        }),
        setupRoute(store, '/members/trainingPlan/edit/:id', {
          component: MemberTrainingPlanBuilder,
          moduleName: 'content',
          role: ['member']
        }),
        setupRoute(store, '/members/trainingPlan/view/:id', {
          component: TrainingPlanViewer,
          moduleName: 'content',
          role: ['member']
        }),
        setupRoute(store, '/members/:id', {
          component: MemberPlatformRoute, role: ['member'],
          children: [
            setupRoute(store, 'member_activities', {
              component: ReadonlyActivities,
              module: MemberActivitiesModule,
              role: ['member']
            }),
            setupRoute(store, 'status', { component: ReadonlyStatus, role: ['member'] }),
            setupRoute(store, 'checkups', { component: ReadonlyCheckups, module: CheckupsModule, role: ['member'] }),
            setupRoute(store, 'training', {
              component: MemberActiveTrainingsWorkouts,
              moduleName: 'content',
              role: ['member']
            }),
            setupRoute(store, 'account', { component: MemberPersonalAccount, role: ['member'] }),
            setupRoute(store, 'health', { component: ReadonlyHealthLimitations, role: ['member'] }),
            setupRoute(store, 'body-measurements', { component: ReadonlyMeasurements, role: ['member'] }),
            setupRoute(store, 'contact', { component: MembersSupport, moduleName: 'content', role: ['member'] }),
            setupRoute(store, 'history', {
              component: ResultsHistory, role: ['member'],
              children: [
                setupRoute(store, '', { component: TrainingHistory, role: ['member'] })
              ]
            }),
            setupRoute(store, 'my-club', { component: MyClub, moduleName: 'content', role: ['member'] })
          ]
        }),
        setupRoute(store, '/members/:id/training/workout-play-ground/:id', { component: WorkoutPlayground, role: ['member'] }),
        setupRoute(store, '/members/checkup/:id/:checkupId', {
          component: ReadonlyCheckupWizard, role: ['member'],
          children: [
            setupRoute(store, 'goals', { component: WizardHealthGoals, role: ['member'] }),
            setupRoute(store, 'body-measurements', { component: MemberWizardMeasurements, role: ['member'] }),
            setupRoute(store, 'health', { component: MemberWizardHealthLimitations, role: ['member'] }),
            setupRoute(store, 'training', { component: WizardTrainings, moduleName: 'content', role: ['member'] })
          ]
        }),

        setupRoute(store, '/pattern-library', { component: PatternLibraryRoute, role: ['admin', 'assistant', 'therapist', 'trainer'] }),

        setupRoute(store, '/app', { component: DownloadMobileApp }),
        setupRoute(store, '/*', { component: LoginRoute })
      ]
    })

    sync(store, this)
  }

}

function resetOnArrival (store, from, to, reset: string, dontReset, filterActionName, filterObject) {
  if (to.path === reset && !from.path.includes(reset) && !dontReset.some((path) => from.path.includes(path))) {
    store.dispatch(filterActionName, filterObject)
  }
}

function resetFilters (store, from, to) {
  resetOnArrival(store, from, to, '/trainers/platform/members', [], 'filterMembers', defaultMemberFilters())
  resetOnArrival(store, from, to, '/trainers/platform/content/workouts', ['/trainers/workout'], 'filterWorkouts', defaultWorkoutFilters())
  resetOnArrival(store, from, to, '/trainers/platform/content/trainingplan', ['/trainers/trainingPlan'], 'filterTrainingPlans', defaultTrainingPlanFilters())
}

export type AllowedRoles = 'admin' | 'member' | 'assistant' | 'therapist' | 'trainer' | undefined
type CustomRouteConfig = {
  component: any
  module?: any
  moduleName?: string
  role?: AllowedRoles[]
  children?: RouteConfig[]
}

function setupRoute (store: Store, path: string, config: CustomRouteConfig): RouteConfig {
  return {
    path: path, components: { default: config.component, cookieBanner: CookieBanner }, children: config.children,
    beforeEnter: async (to, from, next) => {
      if (config.module) {
        store.registerModule(
          config.moduleName ? /* istanbul ignore next: no route has a module with a module name */ config.moduleName : path.replace(/\/(.*)/, '$1').replace('/', '_'),
          new config.module()
        )
      }

      const userRole: AllowedRoles = SessionManager.Instance.isManager() ? SessionManager.Instance.getRole() : 'member'

      // auto login
      if (path === '/') {
        if (SessionManager.Instance.getAuthToken()) {
          if ((['admin', 'assistant', 'therapist', 'trainer'] as AllowedRoles[]).includes(userRole)) {
            next('/trainers/platform/members')
          } else {
            next(`/members/${store.state.user!.id}/training`)
          }
          return
        }
      }

      // login with token if member is redirected from email
      if (!SessionManager.Instance.getAuthToken() && path.match(/members/) && to.query.access_token) {
        await SessionManager.Instance.loginWithToken(to.query.access_token)
            .then(user => {
              if (user.state === UserState.DEACTIVATED) SessionManager.Instance.logout()
              else store.state.user = user
            })
            .catch((err) => console.log('login with token failed ', err))
      }

      // check for routes where token is not requested
      if (!SessionManager.Instance.getAuthToken()) {
        if (path !== '/' && !path.match(/(reset-password|email-update|app)/)) {
          next('/')
          return
        }
      }

      // no user logged in
      if (config && config.role && !config.role.includes(userRole)) {
        SessionManager.Instance.logout()
        store.dispatch('logout')
        next('/')
        return
      }
      if (to.path.match(/trainers\/platform\/members/) && !from.path.match(/trainers\/platform\/members/)) {
        store.state.memberFilters.page = 1
      }

      resetFilters(store, from, to)
      next()
    }
  }
}

export default Router
